/**
 * WaiterScene — интерактивная Spline-сцена «цифровой официант»
 * (21st.dev «splite»), вписанная в editorial-layout «Қара алтын»:
 * слева editorial-копия, справа 3D-персонаж, который следит за курсором.
 *
 * 3D грузится лениво и только когда карточка подъезжает к экрану.
 */
import { useEffect, useRef, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n";
import { useReveal } from "../lib/motion";
import { DEMO_URL } from "../content/contacts";
import { SplineScene } from "./ui/SplineScene";
import { ErrorBoundary } from "./ui/ErrorBoundary";

/**
 * Сцена по умолчанию — публичный интерактивный 3D-персонаж (следит за
 * курсором), оформленный как «цифровой официант» кафе.
 *
 * 🔄 Чтобы поставить собственного официанта: соберите/опубликуйте сцену в
 * Spline (spline.design) → Export → «Public URL» и вставьте сюда её
 * `https://prod.spline.design/<id>/scene.splinecode`. Больше менять ничего
 * не нужно — компонент примет любой валидный scene-URL.
 */
const WAITER_SCENE_URL = "https://prod.spline.design/kZDDjO5HuC9GJUM2/scene.splinecode";

export function WaiterScene() {
  const { t } = useLang();
  const ref = useReveal<HTMLElement>();
  const cardRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  // На тач-устройствах курсор-follow бессмыслен: показываем статичный постер
  // и прячем хинт «двигайте курсором» (заодно не грузим тяжёлый Spline на телефон).
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(hover: none), (pointer: coarse)");
    const update = () => setIsTouch(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
            return;
          }
        }
      },
      { rootMargin: "300px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const w = t.waiter;

  return (
    <section
      id="waiter"
      ref={ref}
      className="relative border-t"
      style={{ borderColor: "var(--line)" }}
    >
      <div
        className="mx-auto w-full max-w-7xl px-5 sm:px-8"
        style={{ paddingTop: "var(--space-section)", paddingBottom: "var(--space-section)" }}
      >
        <div
          ref={cardRef}
          className="reveal waiter-card relative grid overflow-hidden rounded-[28px] lg:grid-cols-2"
          data-reveal=""
        >
          <span className="waiter-spotlight" aria-hidden />

          {/* Левая колонка — editorial-копия */}
          <div className="relative z-10 flex flex-col justify-center p-8 sm:p-12">
            <p className="t-eyebrow mb-6">
              <span className="gold-dot mr-3 inline-block align-middle" />
              {w.eyebrow}
            </p>
            <h2 className="t-display mb-5" style={{ fontSize: "var(--text-title)" }}>
              {w.title}{" "}
              <em className="t-serif-i" style={{ color: "var(--gold-bright)", fontWeight: 500 }}>
                {w.accent}
              </em>
            </h2>
            <p className="mb-7 max-w-md text-base leading-relaxed" style={{ color: "var(--fg-soft)" }}>
              {w.body}
            </p>
            <ul className="mb-8 flex flex-wrap gap-2.5">
              {w.chips.map((chip) => (
                <li
                  key={chip}
                  className="rounded-full border px-3.5 py-1.5 text-xs font-semibold"
                  style={{ borderColor: "var(--gold-line)", color: "var(--gold-bright)" }}
                >
                  {chip}
                </li>
              ))}
            </ul>
            <div>
              <a href={DEMO_URL} target="_blank" rel="noreferrer" className="btn-gold">
                {w.cta}
                <ArrowUpRight className="h-4 w-4" aria-hidden />
              </a>
            </div>
          </div>

          {/* Правая колонка — интерактивный 3D-официант */}
          <div className="relative min-h-[360px] sm:min-h-[440px] lg:min-h-[520px]">
            {isTouch ? (
              <WaiterPoster />
            ) : inView ? (
              <ErrorBoundary
                fallback={<WaiterPoster />}
                onError={(e) => {
                  if (typeof window !== "undefined") {
                    (window as Window & { __splineErr?: string }).__splineErr = e.message;
                  }
                }}
              >
                <SplineScene scene={WAITER_SCENE_URL} className="absolute inset-0 h-full w-full" />
              </ErrorBoundary>
            ) : (
              <WaiterPoster />
            )}
            <span className="waiter-stage-veil" aria-hidden />
            {!isTouch && <span className="waiter-hint">{w.hint}</span>}
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Статичный постер — пока 3D подгружается или если сцена недоступна
 * (нет WebGL / офлайн). Золотой сервировочный купол на золотом свечении:
 * секция всегда выглядит цельной, без белого экрана.
 */
function WaiterPoster() {
  return (
    <div className="waiter-poster" aria-hidden>
      <svg viewBox="0 0 64 64" width="84" height="84" fill="none">
        <path
          d="M11 45 h42"
          stroke="var(--gold)"
          strokeWidth="2.4"
          strokeLinecap="round"
        />
        <path
          d="M14 45 a18 18 0 0 1 36 0"
          stroke="var(--gold-bright)"
          strokeWidth="2.4"
          strokeLinecap="round"
        />
        <path d="M32 23 v-5" stroke="var(--gold)" strokeWidth="2.4" strokeLinecap="round" />
        <circle cx="32" cy="15" r="2.6" fill="var(--gold-bright)" />
      </svg>
    </div>
  );
}
